/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the invitation page component
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import { TileMenuItem } from '@/api/dtos';
import TileDataProvider from '@/components/providers/tiles/TileDataProvider';
import MenuItemContext from '@/contexts/Tiles/MenuItemContext';
import { Grid, LinearProgress } from '@mui/material';
import * as React from 'react';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { ITrial } from '../../TrialsArea';
import TileBody, { FavouriteTile, FavouritesProps, TileBackground } from './TileBody';
import TileHeader from './TileHeader';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */



/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const GetApiLinks = (menuItems?: TileMenuItem[], trial?: ITrial, expandedMenuItemsNonFav?: Record<number, boolean>) => {
    return menuItems?.reduce<string[]>((state, current): string[] => {
        if (current.apiLink) {
            if (current.expandable) {
                if (!!trial?.expandedMenuItemStates &&
                    !!current?.expandStateMenuItemId &&
                    trial?.expandedMenuItemStates[current.expandStateMenuItemId] === true
                ) {
                    return [...state, current.apiLink, ...GetApiLinks(current.subTileMenuItems)]
                }

                if (
                    trial?.favourite !== true &&
                    expandedMenuItemsNonFav &&
                    current.expandStateMenuItemId &&
                    expandedMenuItemsNonFav[current.expandStateMenuItemId] === true
                ) {
                    return [...state, current.apiLink, ...GetApiLinks(current.subTileMenuItems)]
                }
            } else {
                return [...state, current.apiLink, ...GetApiLinks(current.subTileMenuItems)]
            }
        }

        return [...state, ...GetApiLinks(current.subTileMenuItems)]
    }, []) ?? [];
}

// const useApiLinks = (menuItems?: TileMenuItem[], trial?: ITrial) => {
//     return useMemo(() => GetApiLinks(menuItems, trial), [menuItems]);
// }



interface ITileExpandedProps {
    trial: ITrial;
    favourite?: boolean;
    expanded: boolean;
    toggleExpandedMenuItem?: (trialId: number, expandMenuItemId: number) => void | undefined;
    toggleExpanded: Dispatch<SetStateAction<boolean>>;
    toggleFavourite: (id: number) => void;
}

const TileExpanded: React.FunctionComponent<ITileExpandedProps> = ({
    trial,
    favourite,
    expanded,
    toggleExpanded,
    toggleFavourite,
    toggleExpandedMenuItem
}) => {
    const { data: tileInstitutionData, loading: loading } = useContext(MenuItemContext);

    const [expandedMenuItemsNonFav, setExpandedMenuItemsNonFav] = useState<Record<number, boolean> | undefined>(undefined);

    const toggleExpandedMenuItemNonfav = useCallback((expandMenuItemId: number) => {
        setExpandedMenuItemsNonFav((state) => {
            const newState = { ...state };

            if (newState[expandMenuItemId] === undefined) {
                newState[expandMenuItemId] = true;
            } else {
                newState[expandMenuItemId] = !newState[expandMenuItemId];
            }

            return newState;
        });
    }, [setExpandedMenuItemsNonFav]);


    const [apiLinks, setApiLinks] = useState(GetApiLinks(tileInstitutionData?.tileMenuItems, trial));

    useEffect(() => {
        setApiLinks(GetApiLinks(tileInstitutionData?.tileMenuItems, trial, expandedMenuItemsNonFav));
    }, [tileInstitutionData, trial, expandedMenuItemsNonFav]);


        // call context

    return (
        <>
            {
                (loading) && (
                    <Grid
                        {...FavouritesProps}
                        item
                    >
                        <FavouriteTile>  

                            <TileHeader 
                                trialId={trial.id}
                                trialName={trial.name} 
                                protocolId={trial.protocolId}
                                logoLightUrl={trial.logo?.light}
                                logoDarkUrl={trial.logo?.dark}
                                logoDesktopHeight={trial.logo?.desktopHeight}
                                logoDesktopWidth={trial.logo?.desktopWidth}
                                logoMobileHeight={trial.logo?.mobileHeight}
                                logoMobileWidth={trial.logo?.mobileWidth}
                                favourite={favourite ?? false}
                                expanded={expanded}
                                toggle={undefined}
                                toggleFavourite={toggleFavourite}
                            />

                            <LinearProgress color="secondary" />
                            
                            
                            <TileBackground />
                        </FavouriteTile>
                    </Grid>
                ) 
            }
            {
                (!loading && !!tileInstitutionData) && (

                    <>
                        <TileDataProvider trialId={trial.id} apiLinks={apiLinks}>
                            <TileBody 
                                trial={trial}
                                tileInstitutionData={tileInstitutionData}
                                favourite={favourite}
                                expanded={expanded}
                                expandedMenuItemsNonFav={expandedMenuItemsNonFav}
                                toggleExpanded={toggleExpanded}
                                toggleFavourite={toggleFavourite}
                                toggleExpandedMenuItem={toggleExpandedMenuItem}
                                toggleExpandedMenuItemNonfav={toggleExpandedMenuItemNonfav}
                            />
                        </TileDataProvider>
                    </>
                )
            }
        </>
    );

    

};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default TileExpanded;